'use client';

import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Link from 'next/link';
import { AuthApi, AuthQueryOptions } from '@/api';
import { DesktopSideAdBanner } from '@/app/_components/ads';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { Image, PageNavigation } from '@/components/client';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { ROUTES } from '@/constants';
import { useToastPoint } from '@/hooks/useToastPoint';
import { DailyCheckInfoCard } from './_components/DailyCheckInfoCard';
import { DailyCheckStampCard } from './_components/DailyCheckStampCard';
const DailyCheckPage = () => {
  const queryClient = useQueryClient();
  const {
    toastError
  } = useToast();
  const {
    toastPoint
  } = useToastPoint();
  const {
    data: attendanceData,
    isLoading: isLoadingAttendance
  } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/point/attendance'](),
    placeholderData: keepPreviousData,
    staleTime: 0,
    select: ({
      context
    }) => {
      return {
        attendanceList: context?.attendanceList.map(item => ({
          checked: item.attendanceYN,
          date: item.date
        })),
        count: context?.attendanceCount,
        month: context?.month,
        point: context?.totalPoints
      };
    }
  });
  const {
    mutateAsync: mutateTodayAttendance
  } = useMutation({
    mutationFn: () => AuthApi['post:/wello/v2/auth/point/attendance'](),
    onError: error => {
      toastError({
        error
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries();
    }
  });
  const handleCheck = async () => {
    try {
      const response = await mutateTodayAttendance();
      const context_0 = response?.data?.context;
      if (context_0?.addPoints && context_0?.resultYN) {
        toastPoint(context_0.addPoints);
      }
    } catch (error_0) {
      toastError({
        error: error_0
      });
    }
  };
  const today = dayjs().date();
  const todayAttendance = attendanceData?.attendanceList?.find(attendance => attendance.date === today);
  const canCheckAttendance = todayAttendance?.checked === false;
  return <div className="grow bg-Gray-100">
      <DesktopPageShell className="grow pt-40 mobile-tablet:pt-0" mainClassName="flex flex-col bg-Gray-100" rightAside={<DesktopSideAdBanner />}>
        <div className="mx-auto flex w-full max-w-screen-sm flex-col">
          <PageNavigation className="bg-Gray-100 pb-12" />
          <section className="mb-20 flex justify-between px-20">
            <div className="mb-64">
              <h1 className="mb-12 font-700 typo-h3">
                매일매일
                <br />
                출석체크
              </h1>
              <p className="typo-body3">
                매일 출석하고
                <br />
                포인트를 적립하세요!
              </p>
            </div>
            <div className="flex items-end">
              <Image alt="달력" height={153} src="/assets/pngs/attendance/calendar.png" style={{
              objectFit: 'contain'
            }} width={134} preload unoptimized />
            </div>
          </section>

          <div className="px-20 pb-42">
            <DailyCheckInfoCard className="mb-20" count={attendanceData?.count} month={attendanceData?.month} point={attendanceData?.point} />
            <DailyCheckStampCard attendanceList={attendanceData?.attendanceList} isLoading={isLoadingAttendance} />
          </div>
        </div>
        <section className="grow bg-Gray-200 pb-120">
          <div className="mx-auto max-w-screen-sm px-20 py-40">
            <p className="mb-8 font-700 text-Gray-700 typo-body3">
              확인해 주세요!
            </p>
            <ul className="mb-24 flex list-none flex-col gap-8 pl-5">
              <li className="flex min-h-20 text-start font-400 text-Gray-700 typo-body4 before:mr-6 before:content-['•'] before:typo-body4">
                매일 5~50P 사이 랜덤으로 지급됩니다.
              </li>
              <li className="flex min-h-20 text-start font-400 text-Gray-700 typo-body4 before:mr-6 before:content-['•'] before:typo-body4">
                7일 연속 출석시 보너스 포인트를 드려요. 연속출석 포인트에 대한
                자세한 내용은 &apos;웰포인트 유의사항&apos;을 확인해주세요.
              </li>
              <li className="flex min-h-20 text-start font-400 text-Gray-700 typo-body4 before:mr-6 before:content-['•'] before:typo-body4">
                <Link className="underline" href={ROUTES.POINTS_GUIDELINES.pathname}>
                  웰포인트 유의사항
                </Link>
                을 확인하세요
              </li>
              <li className="flex min-h-20 text-start font-400 text-Gray-700 typo-body4 before:mr-6 before:content-['•'] before:typo-body4">
                부정행위 또는 허위사실 게재를 통한 웰포인트 획득은 불법 행위로
                사전 경고 없이 이용 정지 처리됩니다.
              </li>
              <li className="flex min-h-20 text-start font-400 text-Gray-700 typo-body4 before:mr-6 before:content-['•'] before:typo-body4">
                출석체크 이벤트는 내부 사정에 따라 사전 고지 없이 종료될 수
                있습니다.
              </li>
            </ul>
            <Button className="hidden w-full xl:flex" disabled={!canCheckAttendance} theme="primary-fill" onClick={handleCheck}>
              {canCheckAttendance ? '출석하고 웰포인트 받기!' : '참여 완료'}
            </Button>
          </div>
        </section>
        <BottomNavPortal wrapperClassName="bg-Gray-200 xl:hidden">
          <footer className="flex w-full gap-8 bg-Gray-200 p-20">
            <Button className="flex-1" disabled={!canCheckAttendance} theme="primary-fill" onClick={handleCheck}>
              {canCheckAttendance ? '출석하고 웰포인트 받기!' : '참여 완료'}
            </Button>
          </footer>
        </BottomNavPortal>
      </DesktopPageShell>
    </div>;
};
export default DailyCheckPage;